import React from 'react';
import { UIComponent } from '@/components/core/components';
import { ProductType, RatingType } from '@/@types';
import clsx from 'clsx';
import { useLingui } from '@lingui/react';
import * as styles from './ratings.styles';
import { Icon, Image } from '../../atoms';

const RatingBySource: {
  [source: string]: UIComponent<{ rating: RatingType; extra?: any }>;
} = {
  default: ({ rating, className }) => {
    return (
      <div className={clsx(styles.item, className)}>
        <Image alt={rating.source} src={`/logos/${rating.source}.png`} />
        {rating.value >= 0 && (
          <span className={styles.value}>
            {rating.value}
            {rating.unit ||
              (rating.total && (
                <>
                  <span className={styles.separator}>/</span>
                  <span className={styles.total}>{rating.total}</span>
                </>
              ))}
          </span>
        )}
      </div>
    );
  },
  michelin: ({ rating }) => {
    if (rating.value === -1) {
      return null;
    }
    return (
      <div className={clsx(styles.item)}>
        <Image alt={rating.source} src="/logos/michelin.png" />
      </div>
    );
  },
  google: ({ rating, className, extra }) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { i18n } = useLingui();
    return (
      <div className={clsx(styles.item, className)}>
        <Image alt={rating.source} src="/logos/google.png" />
        <span className={styles.google}>{rating.value}</span>
        {rating.votes && (
          <>
            <Icon name="star-fill" style={{ color: '#FFC100' }} />
            <span className={styles.google}>({new Intl.NumberFormat(i18n.locale, { notation: 'compact' }).format(rating.votes)})</span>
          </>
        )}
        {extra?.price && (
          <>
            <span>·</span>
            <span className={styles.price}>({extra.price})</span>
          </>
        )}
      </div>
    );
  },
};

export const RatingItem: UIComponent<{ extra?: any; rating: RatingType }> = ({ rating, className }) => {
  if (rating.value < 0) {
    return null;
  }
  const Component = RatingBySource[rating.source] || RatingBySource.default;
  return <Component className={className} rating={rating} key={rating.source} />;
};

const Ratings: UIComponent<
  { extra?: ProductType['extraInfo']; ratings?: ProductType['extraInfo']['ratings'] },
  'div',
  { Item: typeof RatingItem }
> = ({ ratings, className, children, extra }) => {
  return (
    <div className={clsx(styles.items, className)}>
      {children ||
        ratings?.map((rating) => {
          return <RatingItem extra={extra} rating={rating} key={rating.source} />;
        })}
    </div>
  );
};

Ratings.Item = RatingItem;

export default Ratings;
